/**
 * @fileoverview Popover de notificações da aplicação.
 *
 * Exibe lista de notificações com ícones por tipo (atribuição, menção, atrasado),
 * tempo relativo ("agora", "5min", "2h", "3d"), indicador de não lido,
 * e ações de marcar como lido, limpar tudo e ativar push notifications.
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckSquare, MessageCircle, Clock, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { linkifyText } from '@/lib/linkify';

interface Notification {
  id: string;
  type: string;
  title: string;
  body?: string | null;
  is_read: boolean;
  created_at: string;
  /** Optional entity references — when present, the row click can
   *  deep-link into the matching page. */
  entity_type?: string | null;
  entity_id?: string | null;
}

interface AppNotificationsProps {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  pushEnabled: boolean;
  enablePush: () => void;
}

/**
 * Bucket a notification's createdAt into a group label so the list
 * can be split with section headers. Buckets: Hoje, Ontem, Esta semana,
 * Antigas. Older-first within each bucket isn't required since the
 * list arrives sorted desc and we just preserve order.
 */
function groupForDate(iso: string): 'today' | 'yesterday' | 'week' | 'older' {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'older';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (t >= startOfToday) return 'today';
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  if (t >= startOfYesterday) return 'yesterday';
  const startOfWeek = startOfToday - 7 * 24 * 60 * 60 * 1000;
  if (t >= startOfWeek) return 'week';
  return 'older';
}

const GROUP_LABELS: Record<ReturnType<typeof groupForDate>, string> = {
  today: 'Hoje',
  yesterday: 'Ontem',
  week: 'Esta semana',
  older: 'Antigas',
};

/**
 * Popover de notificações com badge de contagem, lista scrollável e ações rápidas.
 * Calcula tempo relativo (agora, min, h, d) para cada notificação.
 */
export function AppNotifications({
  notifications,
  unreadCount,
  markAsRead,
  markAllAsRead,
  clearAll,
  pushEnabled,
  enablePush,
}: AppNotificationsProps) {
  const navigate = useNavigate();
  // Routes notification clicks to the matching detail page based on
  // entity_type. Lead clicks reuse the existing legacy:focus-lead event
  // so Pipeline opens the detail dialog (same path as Foco do dia and
  // the AddLeadDialog "Ver lead" toast). Other types just navigate to
  // the listing — fine fallback when there's no per-id deep-link yet.
  const goToEntity = (n: Notification) => {
    if (!n.entity_type || !n.entity_id) return;
    switch (n.entity_type) {
      case 'lead':
        navigate('/pipeline');
        requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent('legacy:focus-lead', { detail: { dealId: n.entity_id } }));
        });
        break;
      case 'client':
        navigate('/clientes');
        break;
      default:
        // Unknown — no nav, just markAsRead behavior.
        break;
    }
  };

  // Type filter chip — null means "all types". Counts derived from
  // the full list so the chip badges always show absolute numbers.
  // Persisted in localStorage so the user's last lens survives reloads.
  const FILTER_KEY = 'bl-notif-type-filter';
  const [typeFilter, setTypeFilter] = useState<string | null>(() => {
    try { return localStorage.getItem(FILTER_KEY) || null; }
    catch { return null; }
  });
  const setPersistedFilter = (v: string | null) => {
    setTypeFilter(v);
    try {
      if (v) localStorage.setItem(FILTER_KEY, v);
      else localStorage.removeItem(FILTER_KEY);
    } catch { /* empty */ }
  };
  const typeCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const n of notifications) c[n.type] = (c[n.type] || 0) + 1;
    return c;
  }, [notifications]);
  // Distinct types with at least one notification — ordered so the
  // common ones come first; unknown types fall back to alphabetical.
  const TYPE_ORDER = ['assignment', 'mention', 'overdue', 'info'];
  const visibleTypes = Object.keys(typeCounts).sort((a, b) => {
    const ai = TYPE_ORDER.indexOf(a);
    const bi = TYPE_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  const TYPE_LABELS: Record<string, string> = {
    assignment: 'Atribuições',
    mention: 'Menções',
    overdue: 'Atrasos',
    info: 'Info',
  };
  const filteredNotifications = typeFilter
    ? notifications.filter(n => n.type === typeFilter)
    : notifications;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground relative h-8 w-8" aria-label="Notificações">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center border-2 border-card">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 md:w-96 p-0" align="end" sideOffset={8}>
        <div className="flex items-center justify-between p-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-secondary" />
            <h3 className="text-sm font-semibold text-foreground">Notificações</h3>
            {unreadCount > 0 && <Badge className="bg-secondary/10 text-secondary text-[10px]">{unreadCount}</Badge>}
          </div>
          <div className="flex items-center gap-1">
            {(() => {
              // When a type filter is active, scope bulk-mark to the
              // visible (filtered) unreads via per-row markAsRead. When
              // unfiltered, fall back to the parent's markAllAsRead which
              // can issue a single bulk DB update.
              const filteredUnreads = filteredNotifications.filter(n => !n.is_read);
              return filteredUnreads.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[10px] h-6 px-2 text-muted-foreground"
                  onClick={() => {
                    if (typeFilter) {
                      filteredUnreads.forEach(n => markAsRead(n.id));
                    } else {
                      markAllAsRead();
                    }
                  }}
                >
                  Marcar {typeFilter ? 'visíveis' : 'tudo'} lido
                </Button>
              );
            })()}
            {/* Limpar deletes; we keep it scoped to "all" since the
                parent's clearAll uses a single DB delete-by-user_id and
                a per-id loop would be expensive without a bulk endpoint.
                When filter is active, the button label flips so the
                user knows it still wipes everything. */}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-[10px] h-6 px-2 text-muted-foreground"
                onClick={clearAll}
                title={typeFilter ? 'Limpa todas as notificações (não só as filtradas)' : undefined}
              >
                {typeFilter ? 'Limpar todas' : 'Limpar'}
              </Button>
            )}
            {!pushEnabled && (
              <Button variant="ghost" size="sm" className="text-[10px] h-6 px-2 text-secondary gap-1" onClick={enablePush}>
                <Bell className="h-3 w-3" />
                Ativar Push
              </Button>
            )}
          </div>
        </div>
        {visibleTypes.length > 1 && (
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border overflow-x-auto scrollbar-thin">
            <button
              type="button"
              onClick={() => setPersistedFilter(null)}
              className={cn(
                'inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-medium border transition-colors shrink-0',
                typeFilter === null
                  ? 'bg-secondary/15 border-secondary/40 text-secondary'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground',
              )}
            >
              Todas <span className="tabular-nums opacity-70">{notifications.length}</span>
            </button>
            {visibleTypes.map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setPersistedFilter(t === typeFilter ? null : t)}
                className={cn(
                  'inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-medium border transition-colors shrink-0',
                  typeFilter === t
                    ? 'bg-secondary/15 border-secondary/40 text-secondary'
                    : 'bg-card border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {TYPE_LABELS[t] || t}
                <span className="tabular-nums opacity-70">{typeCounts[t]}</span>
              </button>
            ))}
          </div>
        )}
        <ScrollArea className="max-h-[360px]">
          <div className="p-2 space-y-0.5">
            {(() => {
              // Group preserving the upstream desc order. We render
              // section headers between groups; only show a header
              // when the bucket actually has items.
              const grouped: Record<ReturnType<typeof groupForDate>, Notification[]> = {
                today: [], yesterday: [], week: [], older: [],
              };
              for (const n of filteredNotifications) {
                grouped[groupForDate(n.created_at)].push(n);
              }
              const order: (keyof typeof grouped)[] = ['today', 'yesterday', 'week', 'older'];
              return order.flatMap(key => {
                const items = grouped[key];
                if (items.length === 0) return [];
                const unreadInBucket = items.filter(it => !it.is_read);
                return [
                  <div key={`hdr-${key}`} className="pt-2 pb-1 px-1.5 flex items-center justify-between">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                      {GROUP_LABELS[key]}
                    </span>
                    {unreadInBucket.length > 0 && (
                      <button
                        type="button"
                        className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => unreadInBucket.forEach(u => markAsRead(u.id))}
                        title={`Marcar ${unreadInBucket.length} ${GROUP_LABELS[key].toLowerCase()} como lida${unreadInBucket.length > 1 ? 's' : ''}`}
                      >
                        Marcar como lidas
                      </button>
                    )}
                  </div>,
                  ...items.map(n => {
                    const iconMap: Record<string, typeof Bell> = {
                      assignment: CheckSquare,
                      mention: MessageCircle,
                      overdue: Clock,
                      info: Bell,
                    };
                    const colorMap: Record<string, string> = {
                      assignment: 'hsl(var(--secondary))',
                      mention: 'hsl(226, 100%, 51%)',
                      overdue: 'hsl(0, 84%, 60%)',
                      info: 'hsl(var(--muted-foreground))',
                    };
                    const Icon = iconMap[n.type] || Bell;
                    const color = colorMap[n.type] || 'hsl(var(--muted-foreground))';
                    const timeAgo = (() => {
                      const diff = Date.now() - new Date(n.created_at).getTime();
                      const mins = Math.floor(diff / 60000);
                      if (mins < 1) return 'agora';
                      if (mins < 60) return `${mins}min`;
                      const hrs = Math.floor(mins / 60);
                      if (hrs < 24) return `${hrs}h`;
                      return `${Math.floor(hrs / 24)}d`;
                    })();
                    const isNavigable = !!(n.entity_type && n.entity_id);
                    return (
                      <div
                        key={n.id}
                        className={cn(
                          "group flex items-start gap-2.5 p-2 rounded-lg transition-colors cursor-pointer",
                          // Navigable rows lean toward "actionable" with a
                          // tinted hover; non-navigable rows just dim.
                          isNavigable
                            ? "hover:bg-secondary/10 hover:ring-1 hover:ring-secondary/20"
                            : "hover:bg-muted/50",
                          !n.is_read && "bg-secondary/5"
                        )}
                        onClick={() => { markAsRead(n.id); goToEntity(n); }}
                      >
                        <div className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: color + '15' }}>
                          <Icon className="h-3 w-3" style={{ color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className={cn("text-xs font-semibold text-foreground truncate", n.is_read && "text-muted-foreground font-medium")}>{n.title}</p>
                            {!n.is_read && <div className="h-1.5 w-1.5 rounded-full bg-secondary shrink-0" />}
                          </div>
                          {n.body && <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5 break-words">{linkifyText(n.body)}</p>}
                          <p className="text-[10px] text-muted-foreground/70 mt-0.5">{timeAgo}</p>
                        </div>
                        {isNavigable && (
                          <ChevronRight
                            className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0 mt-2 group-hover:text-secondary group-hover:translate-x-0.5 transition-all"
                            aria-hidden
                          />
                        )}
                      </div>
                    );
                  }),
                ];
              });
            })()}
            {filteredNotifications.length === 0 && (
              <div className="text-center py-8 space-y-2">
                <Bell className="h-8 w-8 text-muted-foreground/30 mx-auto" />
                <p className="text-sm text-muted-foreground">
                  {typeFilter ? `Nenhuma "${TYPE_LABELS[typeFilter] || typeFilter}"` : 'Nenhuma notificação'}
                </p>
                {typeFilter && (
                  <button
                    type="button"
                    className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                    onClick={() => setPersistedFilter(null)}
                  >
                    Limpar filtro
                  </button>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
